import { useCallback, useMemo, useState } from 'react';
import { TraceabilityApiClient } from '@/features/traceability/api/TraceabilityApiClient';
import { errorResult, runJob } from '@/lib/http/runJob';
import type { AllToolsResponse } from '@/features/traceability/api/TraceabilityResponseType';
import type { Tool, Trace } from '@/features/traceability/type/Tool';
import { useApiCall } from '@/hooks/useApiCall';
import { useApiClient } from '@/hooks/useApiClient';

export function useComparisonToolsFetch(): {
  tools: Tool[];
  isLoading: boolean;
  handleRefresh: (resetSelectionRefs?: () => void) => void;
} {
  const [tools, setTools] = useState<Tool[]>([]);
  const [refreshTick, setRefreshTick] = useState(0);
  const http = useApiClient();

  const traceabilityApiClient = useMemo<TraceabilityApiClient | null>(
    () => (http ? new TraceabilityApiClient(http) : null),
    [http]
  );

  const fetchTools = useCallback(
    async (signal: AbortSignal) => {
      if (!traceabilityApiClient) {
        return errorResult('TraceabilityApiClient not initialized', 'InitializationError');
      }

      return runJob<AllToolsResponse>({
        initFn: () => traceabilityApiClient.toolReadAll(undefined, { signal }),
        statusFn: (jobId) => traceabilityApiClient.toolsStatus({ jobId }, { signal }),
        signal,
        fallbackMessage: 'Failed to load tools',
      });
    },
    [traceabilityApiClient]
  );

  const { isLoading } = useApiCall<AllToolsResponse>({
    apiFn: fetchTools,
    deps: [refreshTick],
    immediate: !!traceabilityApiClient,
    onSuccess: (data) => {
      const fetchedTools = data.tools
        .filter((tool): tool is Tool => !!tool)
        .map((tool) => ({
          ...tool,
          traces: Array.isArray(tool.traces)
            ? (tool.traces as Trace[]).filter((trace): trace is Trace => !!trace)
            : [],
        }));

      setTools(fetchedTools);
    },
    onError: () => {
      setTools([]);
    },
  });

  const handleRefresh = useCallback(
    (resetSelectionRefs?: () => void) => {
      if (!traceabilityApiClient) {
        return;
      }
      resetSelectionRefs?.();
      setRefreshTick((value) => value + 1);
    },
    [traceabilityApiClient]
  );

  return { tools, isLoading, handleRefresh };
}
